import React from 'react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Plus, X } from 'lucide-react';
import SizePopover from './SizePopover';
import MultiColorSizePopover from './MultiColorSizePopover';
import { rowTotalQty } from './helpers';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MobileOrderList = ({
  rows, updateRow, selectDesign, addRow, setDeleteConfirm,
  filteredDesigns, getBrandName, products,
  toggleSize, applyBand, setSizeQty, setSizeRate, selectAllSizes, clearSizes,
  grandQty, grandTotal,
  getSizesForColor, applyMultiColor, clearColorPicker, clearSizePicker, setMasterQty, onSizesDone, copyPrevSizes,
}) => (
  <div className="space-y-3" data-testid="so-mobile-list">
    {rows.map((row, idx) => {
      const selSizes = Object.keys(row.sizeQty || {});
      const rates = selSizes.map(s => (row.sizeRate || {})[s]);
      const uniformRate = rates.length > 0 && rates.every(r => String(r) === String(rates[0]));
      const mergedQty = rowTotalQty(row);
      return (
        <Card key={idx} className="border-gray-200" data-testid={`so-card-${idx}`}>
          <CardContent className="p-3 space-y-3">
            {/* Header: Sr + design search + remove */}
            <div className="flex items-center gap-2">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-sky-100 text-sky-700 text-sm font-bold flex items-center justify-center">{row.sr}</span>
              <div className="relative flex-1" data-so-design-cell>
                <Input
                  value={row.designSearch}
                  onChange={e => { updateRow(idx, 'designSearch', e.target.value); updateRow(idx, 'showDesignDropdown', true); }}
                  onFocus={() => { if (row.designSearch) updateRow(idx, 'showDesignDropdown', true); }}
                  onBlur={() => setTimeout(() => {
                    const q = row.designSearch?.trim().toLowerCase();
                    if (q && !row.design_code) { const exact = products.find(p => p.design_code.toLowerCase() === q); if (exact) selectDesign(idx, exact); }
                    updateRow(idx, 'showDesignDropdown', false);
                  }, 200)}
                  placeholder="Search design no…"
                  className="h-11 text-base"
                  data-testid={`so-design-search-${idx}`}
                />
                {row.showDesignDropdown && row.designSearch && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto left-0">
                    {filteredDesigns(row.designSearch).map(p => (
                      <div key={p.id} className="px-3 py-2.5 hover:bg-sky-50 cursor-pointer text-sm flex items-center gap-2"
                        onMouseDown={(e) => { e.preventDefault(); selectDesign(idx, p); }}
                        data-testid={`so-design-option-${p.id}`}>
                        {p.images?.[0] && <img src={`${API_BASE}${p.images[0]}`} alt="" className="w-9 h-9 rounded object-cover" />}
                        <span className="font-mono font-semibold">{p.design_code}</span>
                        <span className="text-gray-400 text-xs">{p.category}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {row.design_code && <Badge className="bg-blue-100 text-blue-700 text-xs flex-shrink-0">{getBrandName(row.product?.brand_id)}</Badge>}
              {rows.length > 1 && (
                <button onClick={() => setDeleteConfirm({ idx, designCode: row.design_code || 'empty' })} className="flex-shrink-0 text-red-400 hover:text-red-600 p-1.5" title="Remove" data-testid={`so-remove-row-${idx}`}>
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Colour + Sizes */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] font-semibold text-gray-500 uppercase">Colour</label>
                <div className="mt-0.5">
                  <MultiColorSizePopover idx={idx} row={row} sizesForColor={(color) => getSizesForColor(row.product, color)}
                    applyMultiColor={applyMultiColor} large autoOpen={row.openColorPicker} onAutoOpenConsumed={clearColorPicker} />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-500 uppercase">Sizes</label>
                <div className="mt-0.5">
                  <SizePopover idx={idx} row={row} large
                    toggleSize={toggleSize} applyBand={applyBand} setSizeQty={setSizeQty}
                    setSizeRate={setSizeRate} selectAllSizes={selectAllSizes} clearSizes={clearSizes}
                    setMasterQty={setMasterQty} autoOpen={row.openSizePicker} onAutoOpenConsumed={clearSizePicker}
                    onDone={onSizesDone} onOpenEmpty={copyPrevSizes} />
                </div>
              </div>
            </div>

            {/* Qty / Rate / Discount */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[11px] font-semibold text-gray-500 uppercase">Total qty</label>
                <Input disabled value={selSizes.length ? mergedQty : ''} readOnly placeholder="—"
                  className="h-11 text-base text-right mt-0.5 bg-gray-50 font-semibold text-gray-700" data-testid={`so-qty-${idx}`} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-500 uppercase">Rate/pc</label>
                {selSizes.length === 0 ? (
                  <Input disabled className="h-11 text-base text-right mt-0.5" placeholder="—" />
                ) : uniformRate ? (
                  <Input type="number" min="0" step="0.01" value={rates[0] ?? ''} onChange={e => updateRow(idx, 'rate', e.target.value)}
                    className="h-11 text-base text-right mt-0.5" onWheel={e => e.target.blur()} data-testid={`so-rate-${idx}`} />
                ) : (
                  <div className="h-11 flex items-center justify-end text-sm text-amber-600 font-medium mt-0.5" data-testid={`so-rate-mixed-${idx}`}>Mixed</div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-500 uppercase">Disc %</label>
                <Input type="number" min="0" max="100" step="0.5" value={row.discount} onChange={e => updateRow(idx, 'discount', e.target.value)}
                  className="h-11 text-base text-right mt-0.5" onWheel={e => e.target.blur()} data-testid={`so-discount-${idx}`} />
              </div>
            </div>

            {/* Amount */}
            <div className="flex items-center justify-between border-t pt-2">
              <span className="text-xs text-gray-500">Amount</span>
              <span className="text-lg font-bold text-gray-900" data-testid={`so-amount-${idx}`}>{row.amount > 0 ? `₹${row.amount.toLocaleString('en-IN')}` : '—'}</span>
            </div>
          </CardContent>
        </Card>
      );
    })}

    <Button onClick={addRow} variant="outline" className="w-full h-11 gap-2 border-dashed border-green-400 text-green-700 hover:bg-green-50" data-testid="so-add-row-mobile">
      <Plus className="w-4 h-4" /> Add design
    </Button>

    <Card className="bg-sky-50 border-sky-200">
      <CardContent className="p-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-600">Total · {grandQty} pcs</span>
        <span className="text-xl font-bold text-sky-700">₹{grandTotal.toLocaleString('en-IN')}</span>
      </CardContent>
    </Card>
  </div>
);

export default MobileOrderList;
